<template>
  <div class="flex flex-col overflow-hidden rounded-xl bg-white px-6 py-3 shadow-[0px_6px_15px_-2px_rgba(16,24,40,0.08)]">
    <div class="flex flex-col">
      <!-- Doctor -->
      <div class="flex items-start gap-2 py-3.5">
        <div v-if="photoUrl" class="relative size-8 shrink-0 overflow-hidden">
          <img
            :src="photoUrl"
            :alt="displayName"
            class="absolute inset-0 size-full object-cover"
            draggable="false"
          >
        </div>
        <div
          v-else
          class="flex size-8 shrink-0 items-center justify-center rounded bg-neutral-100 text-neutral-400"
          aria-hidden="true"
        >
          <Icon name="user" class="size-4" />
        </div>
        <p class="min-w-0 text-sm leading-4 text-dark">
          <span v-if="specialistTitle" class="text-gray">{{ specialistTitle }}</span>
          <br v-if="specialistTitle" aria-hidden="true">
          <span>{{ displayName }}</span>
        </p>
      </div>

      <!-- Facility -->
      <div v-if="facility" class="flex items-start gap-2 py-3.5">
        <Icon name="location" class="mt-0.5 size-4 shrink-0 text-[#1f2937]" />
        <p class="min-w-0 text-sm leading-4 text-dark">
          {{ facility.name }}
          <br v-if="facility.address" aria-hidden="true">
          <span v-if="facility.address" class="text-gray">({{ facility.address }})</span>
        </p>
      </div>

      <!-- Service -->
      <div v-if="service" class="flex items-center gap-2 py-3.5">
        <Icon name="briefcase" class="size-4 shrink-0 text-[#1f2937]" />
        <p class="min-w-0 text-sm leading-4 text-dark">
          <span>{{ service.name }}</span>
          <span v-if="serviceMeta" class="font-semibold text-[#16a34a]">{{ ' ' }}({{ serviceMeta }})</span>
        </p>
      </div>

      <!-- Date -->
      <div v-if="formattedDate" class="flex items-center gap-2 py-3.5">
        <Icon name="calendar-days" class="size-4 shrink-0 text-[#1f2937]" />
        <p class="text-sm leading-4 text-dark">{{ formattedDate }}</p>
      </div>

      <!-- Payment: PayU prepayment or on-site cash/card (same copy as specialist profile) -->
      <div class="flex items-center gap-2 py-3.5">
        <Icon
          :name="requiresPrepayment ? 'credit-card' : 'banknotes'"
          class="size-4 shrink-0 text-[#1f2937]"
        />
        <p class="text-sm leading-4 text-dark">
          {{
            requiresPrepayment
              ? $t('booking_summary.payment_payu')
              : $t('specialists.payment_on_site')
          }}
        </p>
      </div>

      <Link
        v-if="!bookingResult"
        :href="changeHref"
        class="flex h-10 items-center justify-center gap-2 self-start rounded-lg px-4 py-2.5 text-sm font-medium leading-4 text-[#149dca] transition-colors hover:bg-sky-50"
      >
        {{ $t('booking_summary.change') }}
        <Icon name="cheveron-right" class="size-5 shrink-0 fill-current" />
      </Link>
    </div>

    <div
      v-if="bookingOutput?.paymentUrl"
      class="mt-4 flex flex-col gap-3 border-t border-neutral-100 pt-4"
    >
      <p class="text-sm leading-5 text-neutral-600">
        {{ $t('booking_summary.payment_note') }}
      </p>
      <a
        :href="bookingOutput.paymentUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="flex items-center justify-center gap-2 rounded-lg bg-[#149dca] px-4 py-3 text-sm font-semibold leading-4 text-white transition-colors hover:bg-[#1189b1]"
      >
        {{ $t('booking_details.pay') }}
      </a>
    </div>
  </div>
</template>

<script>
import { Link } from '@inertiajs/vue3';
import Icon from '@/Shared/Icon.vue';
import { formatServiceMeta } from '@/utils/serviceMeta';
import { formatBookingDate } from '@/utils/bookingDate';

export default {
  name: 'BookingSummary',
  components: { Link, Icon },
  props: {
    specialist: {
      type: Object,
      required: true,
    },
    service: {
      type: Object,
      default: null,
    },
    chosenDate: {
      type: String,
      default: null,
    },
    facility: {
      type: Object,
      default: null,
    },
    changeHref: {
      type: String,
      required: true,
    },
    bookingResult: {
      type: Object,
      default: null,
    },
  },
  computed: {
    bookingOutput() {
      return this.bookingResult?.output || null;
    },
    displayName() {
      return this.specialist.full_name || this.specialist.fullName || '';
    },
    specialistTitle() {
      return this.specialist.title || '';
    },
    photoUrl() {
      return this.specialist.photo_url || this.specialist.photo || null;
    },
    requiresPrepayment() {
      return Boolean(this.specialist?.requires_prepayment);
    },
    serviceMeta() {
      return this.service ? formatServiceMeta(this.service) : '';
    },
    formattedDate() {
      return formatBookingDate(this.chosenDate);
    },
  },
};
</script>
